
import { mapState, mapGetters } from "vuex";

import { gtmEventMixin } from "~/mixins";

const SOURCES = ["PDP", "WISHLIST", "QUICK_VIEW"];

export default {
  mixins: [gtmEventMixin],

  props: {
    selectedVariant: { type: Object, required: true },
    product: { type: Object, required: true },
    variantImageSrc: { type: String, default: "" },
    buttonContent: { type: String, default: "" },
    styles: { type: Object, default: () => {} },
    source: {
      type: String,
      default: "PDP",
      validator: source => SOURCES.includes(source),
    },
    externalBespokeSelection: { type: Object, default: null },
  },

  computed: {
    ...mapState("app", ["addingToCartVariants"]),
    ...mapState("bag", ["shop", "miniCart", "bespokeVariants"]),
    ...mapState("pdp", ["mattressProtector", "mattressAndBaseRemoval"]),
    ...mapState("pdp", {
      storeBespokeSelection: state => state.bespokeSelection,
    }),
    ...mapGetters("bag", [
      "visibleLineItems",
      "lines",
      "cartSubTotal",
      "customizedLines",
      "getMattressAndBaseRemovalLine",
    ]),
    ...mapGetters(["getCartButtonContent"]),
    isOutOfStock() {
      return !this.selectedVariant?.availableForSale;
    },
    normalizedLines() {
      const customizedLines = this.customizedLines.map(line => line.id);

      return this.lines
        .filter(line => !customizedLines.includes(line.id))
        .reduce((acc, line) => {
          acc[line.variant.variantId] = line;
          return acc;
        }, {});
    },
    bonusLine() {
      if (!this.selectedVariant.bonus_variant_id) {
        return;
      }

      const { bonus_variant_id: bonusVariantId, sku } = this.selectedVariant;
      const bonusAttributes =
        this.getCartLine(bonusVariantId)?.attributes ?? [];
      const bonusKey = `_bonus_for_${sku}`;

      return {
        variantId: bonusVariantId,
        quantity: 1,
        attributes: bonusAttributes
          .filter(({ key }) => key !== "_time" && key !== bonusKey)
          .reduce(
            (acc, { key, value }) => acc.concat({ key, value }),
            [
              { key: "_time", value: this.getTime() },
              { key: bonusKey, value: sku },
            ]
          ),
      };
    },
    mattressProtectorLine() {
      if (!this.mattressProtector) {
        return;
      }

      const { variantId, compareAtPrice } = this.mattressProtector;

      return {
        variantId,
        quantity: 1,
        attributes: [
          { key: "_time", value: this.getTime() },
          { key: "_compare_at_price", value: String(compareAtPrice * 100) },
        ],
      };
    },
    mattressAndBaseRemovalLine() {
      if (!this.mattressAndBaseRemoval) {
        return null;
      }

      const { merchandiseId, compareAtPrice, quantity, pixelDetails } =
        this.mattressAndBaseRemoval;

      const selectedMainLine = this.getCartLine(this.selectedVariant.variantId);
      const line = this.getMattressAndBaseRemovalLine(selectedMainLine.id);

      return {
        merchandiseId,
        ...(line && { id: line.id }),
        quantity,
        attributes: [
          { key: "_compare_at_price", value: String(compareAtPrice * 100) },
          { key: "_pixel_details", value: JSON.stringify(pixelDetails) },
        ],
      };
    },
    customizationAttrs() {
      const customizations = (this.product?.customizations ?? [])
        ?.filter(c => this.$store.state.pdp.customizations.includes(c.id))
        .map(c => c.id);

      return customizations.length > 0
        ? {
            key: "_added_customization_ids",
            value: JSON.stringify(customizations),
          }
        : null;
    },
    bespokeLine() {
      if (!this.activeBespokeSelection?.optIn) {
        return null;
      }
      const bespokeSku = this.selectedVariant?.bespoke_sku;
      if (!bespokeSku) {
        return null;
      }

      const bespokeVariant =
        this.bespokeVariants?.find(variant => variant.sku === bespokeSku) ||
        null;
      const bespokeVariantId = bespokeVariant?.id;
      if (!bespokeVariantId) {
        return;
      }

      const { id, sku } = this.selectedVariant;
      return {
        variantId: bespokeVariantId,
        quantity: 1,
        attributes: [
          { key: "_time", value: this.getTime() },
          { key: "_bespoke_for_key", value: id },
          { key: "_bespoke_for", value: sku },
        ],
      };
    },
    activeBespokeSelection() {
      return (
        this.externalBespokeSelection ?? this.storeBespokeSelection ?? null
      );
    },
    payload() {
      const { variantId, compareAtPrice } = this.selectedVariant;

      const mainLine = {
        variantId,
        quantity: 1,
        attributes: [
          { key: "_time", value: this.getTime() },
          { key: "_compare_at_price", value: String(compareAtPrice * 100) },
          { key: "_variant_image_src", value: this.variantImageSrc },
          this.customizationAttrs,
        ].filter(Boolean),
      };

      const totalItems = [
        mainLine,
        this.bonusLine,
        this.mattressProtectorLine,
        this.bespokeLine,
      ].filter(Boolean);

      let addPayload = [];
      let updatePayload = [];
      for (const { variantId, quantity, attributes } of totalItems) {
        // For bespoke lines, check for existing line with matching _bespoke_for_key
        const bespokeForKey = attributes.find(
          attr => attr.key === "_bespoke_for_key"
        )?.value;
        let line = null;

        if (bespokeForKey) {
          // Find bespoke line that matches both merchandiseId and _bespoke_for_key
          line = this.lines.find(l => {
            if (l.variant.variantId !== variantId) {
              return false;
            }
            const existingBespokeKey = l.attributes?.find(
              attr => attr.key === "_bespoke_for_key"
            )?.value;
            return existingBespokeKey === bespokeForKey;
          });
        } else {
          // For non-bespoke lines, use existing getCartLine logic
          line = this.getCartLine(variantId);
        }

        const payload = {
          attributes: [
            ...attributes,
            {
              key: "_pixel_details",
              value: JSON.stringify({
                brand: this.product?.brand_list ?? "",
                category: this.product?.product_category ?? "",
              }),
            },
          ].filter(Boolean),
          merchandiseId: variantId,
          quantity: (line?.quantity ?? 0) + quantity,
        };

        line
          ? updatePayload.push({ ...payload, id: line.id })
          : addPayload.push(payload);
      }

      const selectedMainLine = this.getCartLine(this.selectedVariant.variantId);
      if (this.mattressAndBaseRemovalLine) {
        this.mattressAndBaseRemovalLine?.id
          ? updatePayload.push(this.mattressAndBaseRemovalLine)
          : addPayload.push({
              ...this.mattressAndBaseRemovalLine,
              parent: selectedMainLine
                ? { lineId: selectedMainLine.id }
                : { merchandiseId: this.selectedVariant.variantId },
            });
      }

      return { addPayload, updatePayload };
    },
    isDisabled() {
      return this.isOutOfStock || this.isAddingToCart;
    },
    isAddingToCart() {
      return this.addingToCartVariants.includes(this.selectedVariant.variantId);
    },
    productId() {
      return String(this.product.id).replace("gid://shopify/Product/", "");
    },
    tagBasedButtonContent() {
      return this.getCartButtonContent(this.product.tags);
    },
    buttonText() {
      return (
        this.tagBasedButtonContent?.button_text ||
        (this.isOutOfStock ? "out of stock" : this.buttonContent)
      );
    },
    buttonLink() {
      return (
        this.$prismic.asLink(this.tagBasedButtonContent?.button_link) || null
      );
    },
  },

  mounted() {
    this.$emit("gtmProductDetailView");
    this.$store.dispatch("bag/fetchBespokeVariants");
  },

  methods: {
    async addItemToCart() {
      this.$store.dispatch("app/setIsAddingToCart", {
        variantId: this.selectedVariant.variantId,
        isAdding: true,
      });

      try {
        const { addPayload, updatePayload } = this.payload;
        if (!this.miniCart.id) {
          await this.$store.dispatch("bag/CREATE_CART", addPayload);
        } else {
          updatePayload.length &&
            (await this.$store.dispatch(
              "bag/UPDATE_CART_LINES",
              updatePayload
            ));

          addPayload.length &&
            (await this.$store.dispatch("bag/ADD_CART_LINES", addPayload));
        }

        await this.$store.dispatch("app/openAddedToBag", {
          items: [
            this.selectedVariant.variantId,
            this.selectedVariant?.bonus_variant_id,
            this.mattressProtector?.variantId,
          ],
          isTablet: this.source === "WISHLIST" ? false : this.isTablet,
        });
        this.$emit("gtmAddToCartEvent");
        this.gtmAddToCart();
      } catch (err) {
        this.$bugsnag.notify(err);
      }
      this.$store.dispatch("app/setIsAddingToCart", {
        variantId: this.selectedVariant.variantId,
        isAdding: false,
      });
    },
    gtmAddToCart() {
      const mattressProtectorId = this.mattressProtector
        ? this.mattressProtector?.variantId
        : null;

      const itemIds = [this.selectedVariant?.variantId, mattressProtectorId];
      let items = this.lines
        .filter(item => itemIds.includes(item?.variant?.variantId))
        .map(({ variant }) => {
          const { sku, product, title, price, compareAtPrice } = variant;

          return {
            item_id: product.id,
            item_sku: sku,
            item_name: product.title,
            currency: "AUD",
            item_brand: product?.brand_list ?? "",
            item_category: product?.product_category ?? "",
            item_variant: title,
            quantity: 1,
            price,
            discount: compareAtPrice > price ? compareAtPrice - price : 0,
          };
        });

      this.$gtm.gtagEvent("add_to_cart", {
        value: this.selectedVariant.price,
        items,
      });
    },
    getTime() {
      const attributes =
        this.getCartLine(this.selectedVariant.id)?.attributes ?? [];

      return (
        attributes.find(attr => attr.key === "_time")?.value ??
        new Date().getTime().toString()
      );
    },
    handleClick() {
      return this.buttonLink
        ? this.$router.push(this.buttonLink)
        : this.addItemToCart();
    },
    getCartLine(variantId) {
      const normalizeValue = value =>
        value === undefined || value === null || value === false ? "" : value;

      const targetCustomization = normalizeValue(
        this.customizationAttrs?.value
      );

      return this.lines.find(line => {
        if (line.variant.variantId !== variantId) {
          return false;
        }

        const lineCustomization = normalizeValue(
          line.attributes.find(attr => attr.key === "_added_customization_ids")
            ?.value
        );
        if (lineCustomization !== targetCustomization) {
          return false;
        }
        return true;
      });
    },
  },
};
